// @ts-check
// Ação "Não é lead" (docs/arquitetura-appcrm/08-contrato-modelo-de-dados.md, etapa NOT_A_LEAD): sem coluna no kanban,
// o card é classificado pelo menu do card no kanban, pela página do card, pela barra de seleção da tabela, pela área
// de soltar do kanban e pela conversa. Sempre pede o motivo; o card sai do quadro e das estatísticas, sem exclusão.
import { NOT_A_LEAD_STAGE } from '../../../../shared/metadata/funnel-stages.js';
import { navigate } from '../../app/router.js';
import { currentUserPermissionsState, getViewsForObject } from '../../data/metadata-store.js';
import { updateManyRecords } from '../../data/parity.js';
import { updateRecordOptimistically } from '../../data/record-store.js';
import { canUpdateObjectRecords, writableFunnelStages } from '../../lib/permissions.js';
import { enqueueSnackbar, showErrorSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';
import { resolveStageTransition } from './stage-transition.js';

/**
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 */

export { NOT_A_LEAD_STAGE };
const CARD_OBJECT_NAME = 'enrollmentOpportunity';
const STAGE_FIELD_NAME = 'stage';

/**
 * O card do funil tem a etapa (escola já atualizada), a pessoa pode editar e o papel dela pode pôr cards em Não é lead
 * (escopo por etapa, contrato de equipe, seção 3.2).
 * @param {ObjectMetadata | undefined} objectMetadata
 */
export function supportsNotLead(objectMetadata) {
  if (!objectMetadata || objectMetadata.nameSingular !== CARD_OBJECT_NAME || !canUpdateObjectRecords(objectMetadata)) return false;
  if (!(writableFunnelStages(currentUserPermissionsState.value)?.has(NOT_A_LEAD_STAGE) ?? true)) return false;
  const stageField = objectMetadata.fields.find((field) => field.name === STAGE_FIELD_NAME);
  return Boolean(stageField?.options?.some((option) => option.value === NOT_A_LEAD_STAGE));
}

/** @param {ObjectMetadata | undefined} objectMetadata @param {any} record */
export const canClassifyAsNotLead = (objectMetadata, record) => supportsNotLead(objectMetadata) && Boolean(record) && !record.deletedAt && record[STAGE_FIELD_NAME] !== NOT_A_LEAD_STAGE;

/** @param {any} record */
export const isNotLead = (record) => record?.[STAGE_FIELD_NAME] === NOT_A_LEAD_STAGE;

/**
 * Link para a view que lista os cards classificados (a semeada "Não são leads" ou outra com o mesmo filtro).
 * @param {ObjectMetadata} objectMetadata
 */
export function findNotLeadViewHref(objectMetadata) {
  const view = getViewsForObject(objectMetadata.id).find((candidate) => candidate.type === 'TABLE'
    && (candidate.viewFilters ?? []).some((filter) => String(filter.value ?? '').includes(NOT_A_LEAD_STAGE)));
  return view ? `/objects/${objectMetadata.namePlural}?viewId=${view.id}` : null;
}

/** @param {ObjectMetadata} objectMetadata @param {string} message */
function showClassifiedSnackbar(objectMetadata, message) {
  const href = findNotLeadViewHref(objectMetadata);
  enqueueSnackbar({ message, variant: 'success', ...(href ? { buttonLabel: t('notLead.openList'), onButtonClick: () => navigate(href) } : {}) });
}

/**
 * Classifica um card: pede o motivo e grava a etapa. Devolve false se a pessoa cancelar.
 * @param {ObjectMetadata} objectMetadata @param {any} record
 */
export async function classifyRecordAsNotLead(objectMetadata, record) {
  const patch = { [STAGE_FIELD_NAME]: NOT_A_LEAD_STAGE };
  const resolved = await resolveStageTransition(objectMetadata, record, { apiPatch: patch, localPatch: patch });
  if (!resolved) return false;
  try {
    await updateRecordOptimistically(objectMetadata, record.id, resolved.apiPatch, resolved.localPatch);
    showClassifiedSnackbar(objectMetadata, t('notLead.classified'));
    return true;
  } catch (error) {
    showErrorSnackbar(error);
    return false;
  }
}

/**
 * Classifica vários cards com o mesmo motivo (barra de seleção). Cards que o motor recusa (com matrícula) ficam.
 * @param {ObjectMetadata} objectMetadata @param {string[]} ids
 */
export async function classifyRecordsAsNotLead(objectMetadata, ids) {
  const patch = { [STAGE_FIELD_NAME]: NOT_A_LEAD_STAGE };
  const resolved = await resolveStageTransition(objectMetadata, { name: t('notLead.selection', { count: ids.length }) }, { apiPatch: patch, localPatch: patch });
  if (!resolved) return null;
  const result = await updateManyRecords(objectMetadata, ids, resolved.apiPatch);
  if (result.errors.length > 0) {
    enqueueSnackbar({ message: t('notLead.classifiedWithErrors', { count: result.records.length, failed: result.errors.length }), detailedMessage: result.errors[0].message, variant: 'warning' });
  } else {
    showClassifiedSnackbar(objectMetadata, t('notLead.classifiedMany', { count: result.records.length }));
  }
  return result;
}
